import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { ContentService } from '../../services/content.service';
import { DisctionaryContent } from 'src/app/services/sitecoreDictionary/dictionary-content.service';
import { CamService } from 'src/app/services/camService/cam.service';
import { jwtDecode } from 'jwt-decode';
import { ToastrApiService } from 'src/app/shared/common/toastr-api.service';
import { CommonService } from 'src/app/shared/common/common.service';
import { CryptoService } from 'src/app/services/commonService/crypto.service';
import { TravelerManagement } from 'src/app/services/dataLayer/CAM/traveler-management.service';
import { NgIf, NgFor } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-reporting-manager-traveler',
    templateUrl: './reporting-manager-traveler.component.html',
    styleUrls: ['./reporting-manager-traveler.component.scss'],
    imports: [ReactiveFormsModule, NgIf, MatIcon, NgFor, MatFormField, MatLabel, MatInput, MatButton, MatIconButton, MatAutocomplete, MatAutocompleteTrigger, MatOption, MatSpinner]
})
export class ReportingManagerTravelerComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  content: any;
  managerList: any[] = [];
  travelArrangerList: any[] = [];
  filteredManagerList: any[] = [];
  filteredTravelArrangerList: any[] = [];
  managerSearchControl = new FormControl('');
  travelArrangerSearchControl = new FormControl('');
  managerNoItemsText = 'No items found';
  travelArrangerNoItemsText = 'No items found';
  reportingForm!: FormGroup;
  editemployee: boolean = false;
  hasEmployeeData: boolean = false;
  loginType: any


  Loader: any = {
    isSpinner: false,
    isEditableClicked: true
  }

  constructor(
    private readonly contentService: ContentService,
    private readonly dictionaryContentService: DisctionaryContent,
    private readonly fb: FormBuilder,
    private readonly camService: CamService,
    private readonly _ToastrApiService: ToastrApiService,
    private readonly _CommonService: CommonService,
    private readonly crypto: CryptoService,
    private readonly travelerManagement: TravelerManagement
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadContent();

    this.loginType = this.crypto.getValueFromKey('ISCORPTRAVELARRANGER') as string;
    this.enforceReadOnlyForNonAdmin();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ---------------------------
      Initialization Methods
  ----------------------------*/

  private initializeForm(): void {
    this.reportingForm = this.fb.group({
      managerName: [null],
      travelArranger: [null],
    });
  }

  private loadContent(): void {
    combineLatest({
      content: this.contentService.content$,
      dictionary: this.dictionaryContentService.content$
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.content = {
          ...res.content,
          dictionary: res.dictionary
        };
      });
  }

  private async loadInitialData(): Promise<void> {
    const user = this.getUserFromToken();
    if (!user) return;

    await Promise.all([
      this.getManagerList(user),
      this.getManagerArrangerDetails(user)
    ]);

    this.updateSearchDisplays();
    this.syncSearchControlsState();
  }

  /* ---------------------------
      Token Handling
  ----------------------------*/

  private getUserFromToken() {
    const token = localStorage.getItem('QlAccessToken');
    if (!token) return null;

    const decoded: any = jwtDecode(token);

    return {
      subAgentId: decoded?.subAgentId,
      userId: sessionStorage.getItem('userId') || '',
      uid: decoded?.uid,
      role: decoded?.role
    };
  }

  /* ---------------------------
      API Calls
  ----------------------------*/

  private async getManagerList(user: any): Promise<void> {
    try {
      const payload = {
        subagentId: user.subAgentId,
        userRole: user.role,
        b2BUserId: user.userId
      };

      const searchParam = '';

      const [managerRes, arrangerRes] = await Promise.all([
        this.camService.getManagerList(user.subAgentId, user.uid, searchParam, payload),
        this.camService.getTravelArrengerList(user.subAgentId, user.uid, searchParam, payload)
      ]);

      this.managerList = this.excludeCurrentUser(managerRes?.data?.managers ?? []);
      this.travelArrangerList = arrangerRes?.data?.travelArrangers ?? [];
      this.filteredManagerList = [...this.managerList];
      this.filteredTravelArrangerList = [...this.travelArrangerList];

    } catch (error) {
      console.error('Error fetching manager/arranger list:', error);
    }
  }

  private async getManagerArrangerDetails(user: any): Promise<void> {
    try {
      const res = await this.camService.getManagerArrangerDetails(
        user.userId,
        user.subAgentId,
        user.uid
      );

      const responseData = res?.data;

      if (responseData) {
        this.reportingForm.patchValue({
          managerName: responseData.reportingManagerId,
          travelArranger: responseData.travelArrangerId
        });
      }

      this.hasEmployeeData = this._CommonService.hasAnyValue(this.reportingForm.value)// value is in form or not
      if (this.hasEmployeeData) {
        this.Loader.isEditableClicked = true;
        this.reportingForm.disable()
      } else {
        this.Loader.isEditableClicked = false;
        this.reportingForm.enable()

      }

        this.enforceReadOnlyForNonAdmin();
        this.syncSearchControlsState();

    } catch (error) {
      console.error('Error fetching manager details:', error);
      this.enforceReadOnlyForNonAdmin();
      this.syncSearchControlsState();
    }
  }

  onManagerFocus(): void {
    this.filterManagerList((this.managerSearchControl.value ?? '').trim());
  }

  onTravelArrangerFocus(): void {
    this.filterTravelArrangerList((this.travelArrangerSearchControl.value ?? '').trim());
  }

  onManagerSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value.trim();

    if (term === '') {
      this.reportingForm.get('managerName')?.setValue(null);
      this.reportingForm.get('managerName')?.markAsDirty();
    }

    this.filterManagerList(term);
  }

  onTravelArrangerSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value.trim();

    if (term === '') {
      this.reportingForm.get('travelArranger')?.setValue(null);
      this.reportingForm.get('travelArranger')?.markAsDirty();
    }

    this.filterTravelArrangerList(term);
  }

  onManagerSelected(event: MatAutocompleteSelectedEvent): void {
    const user = event.option.value;
    this.reportingForm.get('managerName')?.setValue(user?.b2BUserId ?? null);
    this.reportingForm.get('managerName')?.markAsDirty();
    this.managerSearchControl.setValue(user?.userName ?? '', { emitEvent: false });
  }

  onTravelArrangerSelected(event: MatAutocompleteSelectedEvent): void {
    const user = event.option.value;
    this.reportingForm.get('travelArranger')?.setValue(user?.b2BUserId ?? null);
    this.reportingForm.get('travelArranger')?.markAsDirty();
    this.travelArrangerSearchControl.setValue(user?.userName ?? '', { emitEvent: false });
  }

  clearManager(event: Event): void {
    event.stopPropagation();
    this.reportingForm.get('managerName')?.setValue(null);
    this.reportingForm.get('managerName')?.markAsDirty();
    this.managerSearchControl.setValue('');
    this.filteredManagerList = [...this.managerList];
  }

  clearTravelArranger(event: Event): void {
    event.stopPropagation();
    this.reportingForm.get('travelArranger')?.setValue(null);
    this.reportingForm.get('travelArranger')?.markAsDirty();
    this.travelArrangerSearchControl.setValue('');
    this.filteredTravelArrangerList = [...this.travelArrangerList];
  }

  private filterManagerList(term: string): void {
    this.filteredManagerList = this.filterUserList(this.managerList, term);
  }

  private filterTravelArrangerList(term: string): void {
    this.filteredTravelArrangerList = this.filterUserList(this.travelArrangerList, term);
  }

  /** Hide the opened traveler from manager dropdown (sessionStorage userId vs API b2bUserId). */
  private excludeCurrentUser(users: any[]): any[] {
    const currentUserId = String(sessionStorage.getItem('userId') || '').trim();
    if (!currentUserId) {
      return [...users];
    }
    return users.filter((user) => {
      const id = String(user?.b2bUserId ?? user?.b2BUserId ?? '').trim();
      return id !== currentUserId;
    });
  }

  private filterUserList(users: any[], term: string): any[] {
    const normalizedTerm = term.trim().toLowerCase();

    if (!normalizedTerm) {
      return [...users];
    }

    return users.filter((user) =>
      String(user?.userName ?? '').toLowerCase().includes(normalizedTerm) ||
      String(user?.emailAddress ?? '').toLowerCase().includes(normalizedTerm) ||
      String(user?.employeeCode ?? '').toLowerCase().includes(normalizedTerm)
    );
  }

  private updateSearchDisplays(): void {
    this.updateManagerSearchDisplay();
    this.updateTravelArrangerSearchDisplay();
    this.filteredManagerList = [...this.managerList];
    this.filteredTravelArrangerList = [...this.travelArrangerList];
  }

  private updateManagerSearchDisplay(): void {
    const id = this.reportingForm.get('managerName')?.value;
    if (id === null || id === undefined || id === '') {
      this.managerSearchControl.setValue('', { emitEvent: false });
      return;
    }

    const user = this.managerList.find((item) => String(item?.b2BUserId) === String(id));
    this.managerSearchControl.setValue(user?.userName ?? '', { emitEvent: false });
  }

  private updateTravelArrangerSearchDisplay(): void {
    const id = this.reportingForm.get('travelArranger')?.value;
    if (id === null || id === undefined || id === '') {
      this.travelArrangerSearchControl.setValue('', { emitEvent: false });
      return;
    }

    const user = this.travelArrangerList.find((item) => String(item?.b2BUserId) === String(id));
    this.travelArrangerSearchControl.setValue(user?.userName ?? '', { emitEvent: false });
  }

  private syncSearchControlsState(): void {
    if (this.reportingForm.disabled || !this.isAdminUser) {
      this.managerSearchControl.disable({ emitEvent: false });
      this.travelArrangerSearchControl.disable({ emitEvent: false });
      return;
    }

    this.managerSearchControl.enable({ emitEvent: false });
    this.travelArrangerSearchControl.enable({ emitEvent: false });
  }

  /* ---------------------------
      Save
  ----------------------------*/

  get isAdminUser(): boolean {
    return String(this.loginType) === '0';
  }

  async onSave(): Promise<void> {
    if (!this.isAdminUser) return;
     this.Loader.isSpinner = true;
    const noOfResult = this.managerList?.length || 0;
    this.travelerManagement.reporting_manager_manager_settings_click(noOfResult, 'Save');
    try {
      const user = this.getUserFromToken();
      if (!user) return;

      const payload = {
        managerB2BUserId: this.reportingForm.value.managerName,
        arrangerB2BUserId: this.reportingForm.value.travelArranger
      };

      const res = await this.camService.insertManagerAndTravelarranger(user.subAgentId, user.userId, user.uid, payload);

      if (res.status == "Success") {

        const val = this.reportingForm.value;
        this.hasEmployeeData = true
        if (val.managerName || val.travelArranger) {
          this.reportingForm.disable();
          this.Loader.isEditableClicked = true;
        } else {
          this.reportingForm.enable();
          this.Loader.isEditableClicked = false;
        }

        this.enforceReadOnlyForNonAdmin();
        this.syncSearchControlsState();

        this._ToastrApiService.show('success', res.message);
      }


      console.log('Save response:', res);

    } catch (error) {
      console.error('Error saving manager/arranger:', error);
    }finally{
      this.Loader.isSpinner = false;
    }
  }

  Editemployee() {
    if (!this.isAdminUser) return;

    this.editemployee = true;
    this.Loader.isEditableClicked = false;
    this.reportingForm.enable();
    this.syncSearchControlsState();
  }

  OnCancel() {
    if (!this.isAdminUser) return;

    const noOfResult = this.managerList?.length || 0;
    this.travelerManagement.reporting_manager_manager_settings_click(noOfResult, 'Cancel');
    this.editemployee = false;
    if (this.hasEmployeeData) {
      this.Loader.isEditableClicked = true;
      this.reportingForm.disable()

    } else {

      this.Loader.isEditableClicked = false;
      this.reportingForm.enable()
    }

    this.enforceReadOnlyForNonAdmin();
    this.syncSearchControlsState();
  }

  private enforceReadOnlyForNonAdmin(): void {
    if (!this.isAdminUser) {
      this.Loader.isEditableClicked = true;
      this.reportingForm.disable({ emitEvent: false });
    }
  }

  isSaveEnabled(): boolean {
    const val = this.reportingForm.value;

    const hasValue = val.managerName || val.travelArranger;

    // Agar edit mode me hai → sirf dirty check karo
    if (this.hasEmployeeData) {
      return this.reportingForm.dirty;
    }

    // Agar add mode hai → value hona chahiye
    return !!hasValue;
  }
}